import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { isProviderEnabled } from '@/lib/modelSettings';

// Function to get the appropriate Ollama API URL based on user role
export function getOllamaApiUrl(): string | null {
  // First check for enterprise URL
  const enterpriseApiUrl = localStorage.getItem('ollama_enterprise_api_url');
  if (enterpriseApiUrl) {
    return enterpriseApiUrl;
  }
  // Then fall back to admin URL
  const adminApiUrl = localStorage.getItem('ollama_admin_api_url');
  if (adminApiUrl) {
    return adminApiUrl;
  }
  // For backwards compatibility, check the legacy key
  const legacyApiUrl = localStorage.getItem('ollama_api_url');
  if (legacyApiUrl) {
    return legacyApiUrl;
  }
  // If nothing is set, return null (not the default)
  return null;
}

interface AIConfig {
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
}

// Function to generate chat completions using Ollama API or proxy
export async function generateOllamaCompletion(
  messages: Array<{ role: string; content: string; images?: string[] }>,
  model: string = "llama3",
  source: 'local' | 'remote' = 'local',
  config?: AIConfig
): Promise<{ content: string; tokensUsed: number }> {
  

  const ollamaMessages = messages
    .map(msg => ({
      role: msg.role,
      content: msg.content,
      images: msg.images || []
    }))
    .filter(msg => msg.content || (msg.images && msg.images.length > 0));

  const body: any = {
    model,
    messages: ollamaMessages,
    stream: false,
    options: {
      temperature: config?.temperature,
      num_predict: config?.max_tokens,
      top_p: config?.top_p,
    }
  };

  // Clean up undefined options from the config
  if (body.options.temperature === undefined) delete body.options.temperature;
  if (body.options.num_predict === undefined) delete body.options.num_predict;
  if (body.options.top_p === undefined) delete body.options.top_p;
  if (Object.keys(body.options).length === 0) delete body.options;

  if (source === 'local') {
    const apiUrl = getOllamaApiUrl();
    if (!apiUrl) throw new Error("No local Ollama URL set.");
    try {
      const fullUrl = `${apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl}/api/chat`;
      const response = await fetch(fullUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Ollama API error: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.message && data.message.content) {
        const tokensUsed = (data.prompt_eval_count || 0) + (data.eval_count || 0);
        return { content: data.message.content, tokensUsed };
      } else {
        throw new Error("Unexpected response format from Ollama");
      }
    } catch (error: any) {
      console.error('Error fetching from local Ollama:', error);
      throw error;
    }
  }

  // Otherwise, use the Supabase edge function proxy
  try {
    const { data, error } = await supabase.functions.invoke('ollama-proxy', {
      body
    });

    if (error) {
      console.error('Error invoking ollama-proxy:', error);
      throw new Error(error.message || "Failed to get response from remote Ollama proxy");
    }

    if (!data || data.error) {
      const errorMessage = data?.error || "Failed to get response from remote Ollama proxy";
      throw new Error(errorMessage);
    }

    if (data && data.message && data.message.content) {
      const tokensUsed = (data.prompt_eval_count || 0) + (data.eval_count || 0);
      return { content: data.message.content, tokensUsed };
    } else {
      throw new Error("Unexpected response format from remote Ollama proxy");
    }
  } catch (error: any) {
    console.error('Error fetching from remote Ollama proxy:', error);
    throw error;
  }
}

// Function to validate the Ollama API URL
export async function validateOllamaUrl(url: string): Promise<boolean> {
  try {
    const fullUrl = `${url.endsWith('/') ? url.slice(0, -1) : url}/api/tags`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      return false;
    }

    // Try to parse the response to ensure it's a valid Ollama API
    const data = await response.json();

    // Ollama tags endpoint should return an array of models
    return Array.isArray(data.models);
  } catch (error) {
    console.error('Error validating Ollama URL:', error);
    return false;
  }
}

// Fetch both local and remote Ollama models, tagging each with source
export async function fetchOllamaModels(): Promise<Array<{ name: string; source: 'local' | 'remote' }>> {
  const models: Array<{ name: string; source: 'local' | 'remote' }> = [];
  const localEnabled = isProviderEnabled('ollama_local');
  const remoteEnabled = isProviderEnabled('ollama_remote');

  if (localEnabled) {
    const apiUrl = getOllamaApiUrl();
    if (apiUrl) {
      try {
        const fullUrl = `${apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl}/api/tags`;
        const response = await fetch(fullUrl);
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data.models)) {
            data.models.forEach((model: any) => {
              models.push({ name: model.name, source: 'local' });
            });
          }
        }
      } catch (error) {
        console.error('Error fetching local Ollama models:', error);
      }
    }
  }

  if (remoteEnabled) {
    models.push({ name: 'llama3', source: 'remote' });
  }

  return models;
}